import { useAuth0 } from '@auth0/auth0-react';
import useSWR from 'swr';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

/** How far a queued ATS import has got. */
export type AtsImportState = 'Queued' | 'Running' | 'Completed';

export interface AtsImportJobResponse {
    id: string;
    state: AtsImportState;
    totalApplicationCount: number;
    wouldImportCount: number;
    startedAt: string;
    completedAt: string | null;
}

/** How often the run is re-read while it is still in flight. */
export const ATS_IMPORT_POLL_INTERVAL_MS = 1000;

export const buildAtsImportJobPath = (importJobId: string) => `ats-import-job/${importJobId}`;

/**
 * Whether the run has stopped moving. `undefined` is the first read still being in flight, which is
 * not an answer either way; `null` is a run the backend no longer knows about, which is as finished
 * as a run gets.
 */
export const isAtsImportFinished = (job: AtsImportJobResponse | null | undefined): boolean =>
    job === null || job?.state === 'Completed';

/**
 * One ATS import run, read by id and polled until it reports itself finished.
 */
const useAtsImportJob = (importJobId: string | null) => {
    const { getAccessTokenSilently } = useAuth0();

    return useSWR<AtsImportJobResponse | null>(
        importJobId ? buildAtsImportJobPath(importJobId) : null,
        async (path: string) => {
            try {
                return await http.get<AtsImportJobResponse>({
                    path,
                    accessToken: await getAccessTokenSilently(),
                });
            } catch (error) {
                // A run the backend cannot find is not coming back. Reported as finished rather than
                // rethrown, so a bad id cannot leave the page polling for one every second forever.
                if (error instanceof ResourceNotFoundError) {
                    return null;
                }

                throw error;
            }
        },
        {
            // SWR conditional polling, as useAISearchProgress does it.
            refreshInterval: (job: AtsImportJobResponse | null | undefined) =>
                isAtsImportFinished(job) ? 0 : ATS_IMPORT_POLL_INTERVAL_MS,
            // Below the poll interval on purpose: SWR dedupes a refresh against the previous fetch,
            // so leaving this at its 2s default would silently halve the rate above.
            dedupingInterval: ATS_IMPORT_POLL_INTERVAL_MS / 4,
        },
    );
};

export default useAtsImportJob;
